/**
 * Sidebar context-menu handlers (conversation + module): open-at-cursor,
 * open-below-button, and the action items (rename, archive, pin, delete for
 * conversations; archive + inline-rename for modules, #3598).
 *
 * STATE OWNERSHIP (deliberate): the `contextMenu` / `moduleContextMenu` useState
 * atoms stay in the orchestrator and are threaded in here. They MUST be declared
 * before `useClickOutside` (which closes the menus on outside click) and before
 * `useSidebarWindowEvents` (which receives `setContextMenu`), both of which run
 * earlier in the component than this hook can — this hook needs `userConversations`,
 * `tempConversationOrder`, and `setDeleteConversationTarget`, which are only
 * available after their own hooks/derivations. Moving the state into this hook
 * would force a hook-order change for those earlier consumers, so only the
 * handler bodies are lifted; the two state atoms remain inline.
 *
 * Menu position math is delegated to the `contextMenuGeometry` kernels.
 */

import type React from 'react';
import { useRef } from 'react';
import type { MutableRefObject } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query/keys';
import { confirmDialog } from '@/common/components/ConfirmDialog';
import { archiveModuleWithToast } from '@/modules/_infrastructure/ModuleMenu';
import type { Conversation } from '@/types/conversations';
import { isConversationPinned } from '@/types/conversations';
import { buildPinnedConversationOrder } from '../utils';
import {
  CONVERSATION_MENU_WIDTH,
  CONVERSATION_MENU_HEIGHT,
  MODULE_MENU_WIDTH,
  MODULE_MENU_HEIGHT,
  positionMenuAtCursor,
  positionMenuBelowButton,
  positionMenuCentered,
} from '../contextMenuGeometry';
import type { SidebarModuleItem } from '../types';

type ConversationMenuState = { x: number; y: number; conversation: Conversation } | null;
type ModuleMenuState = { x: number; y: number; item: SidebarModuleItem } | null;

interface UseSidebarContextMenusArgs {
  contextMenu: ConversationMenuState;
  setContextMenu: (value: ConversationMenuState) => void;
  moduleContextMenu: ModuleMenuState;
  setModuleContextMenu: (value: ModuleMenuState) => void;
  token: string | null | undefined;
  workspaceId: string;
  activeConversation: Conversation | null;
  setActiveConversation: (conversation: Conversation | null, options?: { skipFetch?: boolean }) => void;
  queryClient: QueryClient;
  tempConversationOrder: Conversation[] | null;
  userConversations: Conversation[];
  isReadOnly: boolean;
  showToast: (message: string, type?: 'success' | 'info' | 'error') => void;
  setRenamingConversationId: (id: string | null) => void;
  editingConversationNameRefs: MutableRefObject<{ [conversationId: string]: HTMLSpanElement | null }>;
  setDeleteConversationTarget: (value: { id: string; name: string } | null) => void;
  // Inline module rename (#3598): mirrors the conversation rename plumbing.
  setRenamingModuleId: (id: string | null) => void;
  editingModuleNameRefs: MutableRefObject<{ [moduleId: string]: HTMLSpanElement | null }>;
}

export function useSidebarContextMenus({
  contextMenu,
  setContextMenu,
  moduleContextMenu,
  setModuleContextMenu,
  token,
  workspaceId,
  activeConversation,
  setActiveConversation,
  queryClient,
  tempConversationOrder,
  userConversations,
  isReadOnly,
  showToast,
  setRenamingConversationId,
  editingConversationNameRefs,
  setDeleteConversationTarget,
  setRenamingModuleId,
  editingModuleNameRefs,
}: UseSidebarContextMenusArgs) {
  // Guards the contentEditable row's onBlur from double-saving: Enter (and
  // Escape) commit and then call blur() to drop focus, and that synchronous
  // blur fires while `renamingModuleId` is still set (the async save hasn't run
  // setRenamingModuleId(null) yet), so the trailing onBlur would otherwise
  // re-enter handleSaveModuleName and fire a second PATCH for the same label.
  // Mirrors ModuleRenderer's skipTitleBlurRef. (#3598)
  const skipModuleBlurRef = useRef(false);
  const handleConversationContextMenu = (e: React.MouseEvent, conversation: Conversation) => {
    e.preventDefault();
    e.stopPropagation();
    const { x, y } = positionMenuAtCursor(e.clientX, e.clientY, CONVERSATION_MENU_WIDTH, CONVERSATION_MENU_HEIGHT);
    setModuleContextMenu(null);
    setContextMenu({ x, y, conversation });
  };

  const handleConversationActionsClick = (
    e: React.MouseEvent<HTMLButtonElement>,
    conversation: Conversation,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    if (contextMenu?.conversation.id === conversation.id) {
      setContextMenu(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const { x, y } = positionMenuBelowButton(rect, CONVERSATION_MENU_WIDTH, CONVERSATION_MENU_HEIGHT);
    setModuleContextMenu(null);
    setContextMenu({ x, y, conversation });
  };

  const handleRenameFromContextMenu = () => {
    if (!contextMenu) return;
    const conversation = contextMenu.conversation;
    setContextMenu(null);
    setRenamingConversationId(conversation.id);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el = editingConversationNameRefs.current[conversation.id];
        if (!el) return;
        el.focus();
        const selection = window.getSelection();
        if (!selection) return;
        const range = document.createRange();
        range.selectNodeContents(el);
        selection.removeAllRanges();
        selection.addRange(range);
      });
    });
  };

  const handleArchiveFromContextMenu = () => {
    if (!contextMenu) return;
    const conversation = contextMenu.conversation;
    setContextMenu(null);
    void (async () => {
      if (!(await confirmDialog({ message: `Archive "${conversation.name}"?`, confirmLabel: 'Archive' }))) return;
      try {
        await api.workspaces.updateConversation(token ?? null, workspaceId, conversation.id, {
          archived: true,
        });
        if (activeConversation?.id === conversation.id) {
          setActiveConversation(null);
        }
        queryClient.invalidateQueries({ queryKey: queryKeys.conversations.all });
        queryClient.invalidateQueries({ queryKey: queryKeys.conversations.detail(conversation.id) });
        queryClient.invalidateQueries({ queryKey: queryKeys.conversations.lists(), refetchType: 'inactive' });
      } catch (error) {
        console.error('[ConversationSidebar] Failed to archive conversation:', error);
      }
    })();
  };

  const handlePinFromContextMenu = () => {
    if (!contextMenu) return;
    const conversation = contextMenu.conversation;
    const nextPinned = !isConversationPinned(conversation);
    const updatedMeta = {
      ...(conversation.meta ?? {}),
      pinned: nextPinned,
      isPinned: nextPinned,
    };
    const source = tempConversationOrder ?? userConversations;
    setContextMenu(null);

    void (async () => {
      try {
        await api.workspaces.updateConversation(
          token ?? null,
          workspaceId,
          conversation.id,
          { meta: updatedMeta },
        );

        const nextOrderSource = source.map((item) =>
          item.id === conversation.id ? { ...item, meta: updatedMeta } : item,
        );
        const reorderedIds = buildPinnedConversationOrder(nextOrderSource, {
          targetConversationId: conversation.id,
          placeTargetAtTopOf: nextPinned ? 'pinned' : 'unpinned',
        });

        if (reorderedIds.length > 1) {
          try {
            await api.workspaces.reorderConversations(token ?? null, workspaceId, reorderedIds);
          } catch (reorderError) {
            console.error('[ConversationSidebar] Failed to reorder conversations after pin toggle:', reorderError);
          }
        }

        queryClient.invalidateQueries({ queryKey: queryKeys.conversations.all });
        queryClient.invalidateQueries({ queryKey: queryKeys.conversations.detail(conversation.id) });
        queryClient.invalidateQueries({ queryKey: queryKeys.conversations.lists(), refetchType: 'inactive' });
      } catch (error) {
        console.error('[ConversationSidebar] Failed to toggle conversation pin state:', error);
      }
    })();
  };

  const handleModuleLongPress = (item: SidebarModuleItem) => {
    if (isReadOnly) return;
    // Show context menu centered on screen for mobile
    const { x, y } = positionMenuCentered(MODULE_MENU_WIDTH, MODULE_MENU_HEIGHT);
    setContextMenu(null);
    setModuleContextMenu({ x, y, item });
  };

  const handleModuleContextMenu = (e: React.MouseEvent, item: SidebarModuleItem) => {
    e.preventDefault();
    e.stopPropagation();
    const { x, y } = positionMenuAtCursor(e.clientX, e.clientY, MODULE_MENU_WIDTH, MODULE_MENU_HEIGHT);
    setContextMenu(null);
    setModuleContextMenu({ x, y, item });
  };

  const handleModuleActionsClick = (
    e: React.MouseEvent<HTMLButtonElement>,
    item: SidebarModuleItem,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    if (moduleContextMenu?.item.module.id === item.module.id) {
      setModuleContextMenu(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const { x, y } = positionMenuBelowButton(rect, MODULE_MENU_WIDTH, MODULE_MENU_HEIGHT);
    setContextMenu(null);
    setModuleContextMenu({ x, y, item });
  };

  const handleArchiveModuleFromContextMenu = () => {
    if (!moduleContextMenu || !workspaceId) return;
    const item = moduleContextMenu.item;
    setModuleContextMenu(null);
    void archiveModuleWithToast(
      token ?? null,
      workspaceId,
      item.module.id,
      showToast,
      queryClient,
      activeConversation?.id,
      () => {
        window.dispatchEvent(new CustomEvent('xavior:module-archived', { detail: { moduleId: item.module.id } }));
      },
    );
  };

  const handleRenameModuleFromContextMenu = () => {
    if (!moduleContextMenu) return;
    const moduleId = moduleContextMenu.item.module.id;
    setModuleContextMenu(null);
    setRenamingModuleId(moduleId);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el = editingModuleNameRefs.current[moduleId];
        if (!el) return;
        el.focus();
        const selection = window.getSelection();
        if (!selection) return;
        const range = document.createRange();
        range.selectNodeContents(el);
        selection.removeAllRanges();
        selection.addRange(range);
      });
    });
  };

  const revertModuleName = (item: SidebarModuleItem) => {
    const el = editingModuleNameRefs.current[item.module.id];
    if (el) el.textContent = item.displayName;
    setRenamingModuleId(null);
  };

  // Commit an inline module rename (#3598). Persists workspace-scoped and
  // invalidates queryKeys.modules.list — the SAME key the sidebar and the
  // ModuleRenderer header read — so a rename in either surface syncs the other.
  const handleSaveModuleName = async (item: SidebarModuleItem, el: HTMLElement) => {
    const newName = el.textContent?.trim() || '';
    if (!newName || newName === item.displayName) {
      revertModuleName(item);
      return;
    }
    try {
      await api.workspaces.updateWorkspaceModule(token ?? null, workspaceId, item.module.id, {
        label: newName,
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.modules.list(workspaceId) });
      setRenamingModuleId(null);
    } catch (err) {
      console.error('[ConversationSidebar] Failed to update module name:', err);
      revertModuleName(item);
    }
  };

  // Trailing blur after an Enter/Escape commit-then-blur is a no-op: the keydown
  // handler already saved (Enter) or reverted (Escape) and set the guard, so the
  // blur must not fire a second updateWorkspaceModule. (#3598)
  const handleModuleNameBlur = (item: SidebarModuleItem, el: HTMLElement) => {
    if (skipModuleBlurRef.current) {
      skipModuleBlurRef.current = false;
      return;
    }
    void handleSaveModuleName(item, el);
  };

  const handleModuleNameKeyDown = (e: React.KeyboardEvent<HTMLSpanElement>, item: SidebarModuleItem) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      skipModuleBlurRef.current = true;
      void handleSaveModuleName(item, e.currentTarget);
      e.currentTarget.blur();
    } else if (e.key === 'Escape') {
      skipModuleBlurRef.current = true;
      revertModuleName(item);
      e.currentTarget.blur();
    }
  };

  const handleDeleteFromContextMenu = () => {
    if (!contextMenu) return;
    const conversation = contextMenu.conversation;
    setContextMenu(null);
    setDeleteConversationTarget({ id: conversation.id, name: conversation.name });
  };

  return {
    handleConversationContextMenu,
    handleConversationActionsClick,
    handleRenameFromContextMenu,
    handleArchiveFromContextMenu,
    handlePinFromContextMenu,
    handleModuleLongPress,
    handleModuleContextMenu,
    handleModuleActionsClick,
    handleArchiveModuleFromContextMenu,
    handleRenameModuleFromContextMenu,
    handleSaveModuleName,
    handleModuleNameBlur,
    handleModuleNameKeyDown,
    handleDeleteFromContextMenu,
  };
}
